/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { inject as service } from '@ember/service';
import { action } from '@ember/object';
import merge from 'lodash.merge';
import moment from 'moment-timezone';
import { METRIC_COLORS } from 'cio/utils/metrics/colors';
import { MEASURE_LABELS } from 'cio/utils/metrics/analysis';
import { generateTimestamps, Measure } from 'cio/utils/metrics/common';
import { Metric } from 'cio/utils/metrics/models/metric';
import { addDashedZonesToIncompleteDataPoints } from 'cio/utils/graphs';
import { transactionalMetrics } from 'cio/utils/metrics/transactional';
import AnalyticsService from 'cio/services/analytics';
import { Grouping } from 'cio/utils/metrics/resolution';
import type { Options } from 'highcharts';
import { TimeRange } from 'cio/utils/time';
import { isDeliveryMeasureEnabledForChannel } from 'cio/utils/metrics/measure';
import { TransactionalMessageChannelType } from 'cio/models/transactional-message';

interface TransactionalDeliveryMetricsPerformanceChartArgs {
  groups: Record<string, Record<'values' | 'ratios', number[]>>;
  metric: Metric;
  grouping: Grouping;
  timeRange: TimeRange;
  channelType: TransactionalMessageChannelType;
}

const BASE_CHART_OPTIONS: Options = {
  chart: {
    height: 325,
  },
  legend: {
    enabled: false,
  },
  time: {
    timezone: 'Etc/UTC',
  },
  xAxis: {
    type: 'datetime',
    startOfWeek: 1,
  },
  plotOptions: {
    series: {
      zoneAxis: 'x',
      zones: [],
    },
  },
};

type MetricOption = {
  label: string;
  value: string;
  color: { name: string; value: string };
  count: number;
  percentage: number;
};

export default class TransactionalDeliveryMetricsPerformanceChart extends Component<TransactionalDeliveryMetricsPerformanceChartArgs> {
  @service analytics!: AnalyticsService;

  @tracked metrics: MetricOption[] = [];
  @tracked selectedMetrics: string[] = ['sent', 'opened', 'clicked'].filter((metric: Measure) =>
    isDeliveryMeasureEnabledForChannel(metric, 'email'),
  );
  @tracked chartOptions = BASE_CHART_OPTIONS;
  @tracked chartUnit = 'count';

  @action
  setupData() {
    this.setupMetrics();
    this.setupChartOptions();
  }

  @action
  toggleMetric(key: string, checked: boolean) {
    if (checked) {
      this.selectedMetrics = [...this.selectedMetrics, key];
    } else {
      this.selectedMetrics = this.selectedMetrics.filter((m) => m !== key);
    }

    this.setupChartOptions();

    this.analytics.track('transactional', 'metrics_display_option_changed', {
      editType: 'metrics',
      chartUnit: this.chartUnit,
      metrics: this.selectedMetrics,
    });
  }

  @action
  onChartUnitChange(value: string) {
    this.chartUnit = value;
    this.setupData();

    this.analytics.track('transactional', 'metrics_display_option_changed', {
      editType: 'chartUnit',
      chartUnit: this.chartUnit,
      metrics: this.selectedMetrics,
    });
  }

  private setupMetrics() {
    if (!this.args.metric) {
      return;
    }

    this.metrics = transactionalMetrics(this.args.channelType).map((measure, i) => {
      let count = this.args.metric[measure]!.total;
      let percentage = this.args.metric[measure]!.rate;

      return {
        value: measure,
        label: MEASURE_LABELS[`delivery_metrics.${measure}`],
        color: METRIC_COLORS[i % METRIC_COLORS.length],
        count: count ? count : 0,
        percentage: percentage ? percentage : 0,
      };
    });
  }

  private setupChartOptions() {
    let utcRange = {
      start: moment.tz(`${this.args.timeRange.start?.format('YYYY-MM-DD')} 00:00:00`, 'Etc/UTC'),
      end: moment.tz(`${this.args.timeRange.end?.format('YYYY-MM-DD')} 00:00:00`, 'Etc/UTC'),
    };

    let timestamps = generateTimestamps(utcRange, this.args.grouping);
    let series = this.metrics
      .filter((metric) => this.selectedMetrics.includes(metric.value))
      .map((metric) => {
        let group = this.args.groups[metric.value];
        if (!group) {
          return {};
        }

        return {
          type: 'line',
          id: metric.value,
          name: metric.label,
          colorIndex: metric.color.name,
          data: group[this.chartUnit === 'percentage' ? 'ratios' : 'values'].map((value, i) => ({
            x: timestamps[i],
            y: value,
          })),
        };
      });

    let options: Options = merge({}, BASE_CHART_OPTIONS, {
      series,
    });

    if (this.chartUnit === 'percentage') {
      options.yAxis = {
        labels: {
          format: '{value:.1f}%',
        },
      };
      options.tooltip = {
        valueDecimals: 1,
        valueSuffix: '%',
      };
    } else {
      // Need to explicitly reset these values, otherwise highcharts will keep
      // the percentage formatting when switching
      options.yAxis = {
        labels: {
          format: undefined,
        },
      };
      options.tooltip = {
        valueDecimals: undefined,
        valueSuffix: undefined,
      };
    }

    options = addDashedZonesToIncompleteDataPoints(options, timestamps, utcRange, this.args.grouping);
    this.chartOptions = options;
  }
}
